<template>
  <div class="flex flex-col gap-2.5 sm:gap-4 w-full">
    <!-- Top Welcome Banner (Compact & Modern) -->
    <div
      class="bg-gradient-to-r from-indigo-600 via-indigo-700 to-blue-600 rounded-2xl p-4 sm:p-5 text-white shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
      <div>
        <h1 class="text-base sm:text-xl lg:text-2xl font-extrabold tracking-tight mb-0.5 text-white">
          Welcome back, {{ employeeName }} 👋
        </h1>
        <p class="text-indigo-100 text-xs sm:text-sm max-w-xl mb-0">
          Track your daily schedule, clock in your hours, and manage field activities.
        </p>
      </div>
    </div>

    <!-- PRIMARY TIME TRACKER AT THE TOP -->
    <div v-if="shouldShowClockInOut">
      <AppTimeTracker :isCheckedIn="isCheckedIn" :loading="clockInLoading" :initialWorkSeconds="todayWorkSeconds"
        :workingHoursText="todayWorkingHoursText" :customActivitySteps="customActivitySteps"
        @toggle-clock-in="handleClockInToggle" @clock-status-changed="fetchTodayAttendanceData"
        @open-mobile-menu="mobileDrawerOpen = true" />
    </div>

    <!-- Quick Action Shortcuts (Claims, Attendance, All Tasks, My Profile) - DESKTOP ONLY -->
    <div class="hidden lg:block w-full">
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <div v-for="card in quickActionCards" :key="card.title" @click="card.action"
          class="p-3 sm:p-4 bg-white rounded-xl border border-gray-100 shadow-2xs hover:shadow-sm cursor-pointer transition-all hover:-translate-y-0.5 flex flex-col items-center text-center select-none">
          <div :class="card.bgClass" class="w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center mb-2">
            <v-icon small :color="card.iconColor">{{ card.icon }}</v-icon>
          </div>
          <span class="text-xs font-bold text-gray-800 line-clamp-1">{{ card.title }}</span>
        </div>
      </div>
    </div>

    <!-- Main Grid: Mobile order (Search -> Tasks) | Desktop order (Left 2-cols: Tasks | Right 1-col: Activity Stats & Search) -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-2.5 sm:gap-6 items-start">
      <!-- Activity Breakdown & Search / Filters (Desktop Right Column, Mobile Search Only) -->
      <div class="flex flex-col gap-2.5 sm:gap-4 lg:col-span-1 lg:order-2">
        <!-- Activity Breakdown & Task Status Overview (DESKTOP ONLY) -->
        <div class="hidden lg:block bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-sm font-bold text-gray-800 mb-0">Activity Breakdown</h3>
            <v-icon small color="grey">mdi-chart-pie</v-icon>
          </div>

          <div class="space-y-3">
            <div v-for="stat in taskStatsList" :key="stat.label"
              class="flex items-center justify-between p-2.5 rounded-xl bg-gray-50 border border-gray-100 text-xs">
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full" :class="stat.dotColor"></span>
                <span class="font-semibold text-gray-700">{{ stat.label }}</span>
              </div>
              <span class="font-extrabold text-gray-900">{{ stat.count }}</span>
            </div>
          </div>
        </div>

        <!-- Search and filters -->
        <div class="bg-white rounded-2xl shadow-sm border border-gray-100"
          :class="hasSearchOptions ? 'p-3.5 sm:p-4 space-y-3' : 'p-2 sm:p-2.5'">
          <!-- Top Row: Search By Chips Grid (Only rendered if there are search options) -->
          <div v-if="hasSearchOptions" class="flex items-center gap-2">
            <span class="text-xs font-bold text-gray-500 uppercase tracking-wider shrink-0">Search By</span>
            <div class="flex items-center gap-1.5 flex-1 overflow-x-auto pb-0.5 no-scrollbar">
              <button type="button" v-for="(field, i) in searchByFieldsList" :key="field.value"
                @click="activeSearchByField = i" :class="activeSearchByField === i
                  ? 'bg-indigo-600 text-white shadow-xs font-bold'
                  : 'text-gray-600 bg-gray-100 hover:bg-gray-200 font-medium'
                  "
                class="cursor-pointer px-2.5 py-1 rounded-lg text-xs transition-all flex items-center gap-1 select-none border-none shrink-0">
                <v-icon v-if="activeSearchByField === i" x-small color="white">mdi-magnify</v-icon>
                <span>{{ field.label }}</span>
              </button>
            </div>
          </div>

          <!-- Search Input with Clear Button (Only rendered if an active search field option exists) -->
          <div v-if="hasSearchOptions && currentSearchField" class="relative w-full">
            <div class="relative flex items-center">
              <v-icon small color="grey" class="absolute left-3 z-10 pointer-events-none">mdi-magnify</v-icon>
              <input v-model="filters[currentSearchField.value]"
                class="w-full rounded-xl border border-gray-200 pl-9 pr-8 text-xs text-gray-800 bg-gray-50/70 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all h-9"
                :placeholder="currentSearchField.placeholderTxt || 'Search...'" @input="onSearchInput" />
              <button v-if="filters[currentSearchField.value]" type="button"
                class="absolute right-2.5 text-gray-400 hover:text-gray-600 focus:outline-none cursor-pointer border-none bg-transparent flex items-center"
                @click="filters[currentSearchField.value] = ''">
                <v-icon x-small color="grey">mdi-close-circle</v-icon>
              </button>
            </div>
          </div>

          <!-- Sort & Filter Single Row (Clean Mobile Alignment) -->
          <div class="flex items-center gap-2"
            :class="hasSearchOptions ? 'pt-1 border-t border-gray-100' : ''">
            <!-- Sort Dropdown -->
            <div class="flex-1 min-w-0">
              <v-select v-model="filters.sortBy" :items="sortOptions" item-text="label" item-value="value" dense
                outlined hide-details prefix="Sort: " class="rounded-xl text-xs bg-gray-50/60 custom-dense-select"
                @change="applyFiltersAndFetch"></v-select>
            </div>

            <!-- Sort Order Toggle -->
            <v-btn depressed small :color="$vuetify.theme.currentTheme.employeePrimary" class="min-w-0 px-2.5 h-9 rounded-xl white--text shrink-0"
              :title="filters.sortOrder === 'asc' ? 'Ascending Order' : 'Descending Order'" @click="toggleSortOrder">
              <v-icon small color="white">
                {{ filters.sortOrder === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending' }}
              </v-icon>
            </v-btn>

            <!-- Filters Button -->
            <v-btn small depressed :color="isAnyFilterActive ? $vuetify.theme.currentTheme.employeePrimary : 'grey lighten-4'"
              :class="isAnyFilterActive ? 'white--text' : 'grey--text text--darken-3'"
              class="px-3 h-9 rounded-xl text-xs font-bold shrink-0 capitalize" @click="filterDrawerOpen = true">
              <v-icon left x-small :color="isAnyFilterActive ? 'white' : 'grey darken-3'">mdi-filter-variant</v-icon>
              <span>Filters</span>
              <span v-if="activeFilterCount > 0"
                class="bg-amber-400 text-gray-900 rounded-full px-1.5 py-0.2 text-[10px] font-black ml-1">
                {{ activeFilterCount }}
              </span>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Today's Schedule & Tasks Cards (Rendered second for Mobile, shifted to Left Column on Desktop) -->
      <div class="space-y-4 sm:space-y-5 lg:col-span-2 lg:order-1">
        <!-- Today's Schedule Card List Header & Cards -->
        <div id="tasks-schedule-section" class="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div class="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
            <div class="flex items-center gap-2">
              <span class="font-bold text-sm text-gray-800">Today's Schedule</span>
              <span class="text-xs text-blue-600 font-semibold cursor-pointer" @click="resetFilters">
                ({{ filteredTasks.length }} {{ filteredTasks.length === 1 ? 'task' : 'tasks' }})
              </span>
            </div>
            <v-btn text small :color="$vuetify.theme.currentTheme.employeePrimary" class="rounded-lg text-xs font-bold capitalize px-2"
              @click="$router.push('/app/task/create')">
              <v-icon left x-small>mdi-plus</v-icon>
              New Task
            </v-btn>
          </div>

          <!-- Task List Loader -->
          <div v-if="tasksLoading" class="py-12 flex flex-col items-center justify-center">
            <v-progress-circular indeterminate color="indigo" size="36"></v-progress-circular>
            <span class="text-xs text-gray-500 font-medium mt-2">Loading tasks...</span>
          </div>

          <!-- Task Items -->
          <div v-else-if="filteredTasks.length > 0" class="space-y-3">
            <AppTaskCard v-for="task in filteredTasks" :key="task._id || task.id" :task="task"
              @view-details="openTaskDetails" @view-history="openTaskHistory" />
          </div>

          <!-- Empty State -->
          <div v-else class="py-12 flex flex-col items-center justify-center text-center">
            <v-avatar size="48" color="indigo-50" class="mb-3">
              <v-icon color="indigo">mdi-clipboard-text-outline</v-icon>
            </v-avatar>
            <h3 class="text-sm font-bold text-gray-800 mb-1">No Tasks Found</h3>
            <p class="text-xs text-gray-500 max-w-xs mb-3">
              No tasks match your current search and filter settings.
            </p>
            <v-btn small outlined :color="$vuetify.theme.currentTheme.employeePrimary" class="rounded-lg text-xs" @click="resetFilters">
              Reset Filters
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile Bottom Drawer for Quick Action Cards & Activity Breakdown -->
    <v-bottom-sheet v-model="mobileDrawerOpen" max-width="600" content-class="rounded-t-3xl overflow-hidden">
      <v-card class="rounded-t-3xl px-4 py-5 sm:px-6 bg-white max-h-[85vh] overflow-y-auto">
        <!-- Drawer Drag Handle & Header -->
        <div class="flex flex-col items-center mb-3">
          <div class="w-10 h-1 rounded-full bg-gray-300 mb-3"></div>
          <div class="flex items-center justify-between w-full pb-2.5 border-b border-gray-100">
            <div class="flex items-center gap-2">
              <v-icon small color="indigo darken-2">mdi-apps</v-icon>
              <h3 class="text-sm font-bold text-gray-900 mb-0">Quick Shortcuts & Activity</h3>
            </div>
            <button type="button" class="text-gray-400 hover:text-gray-600 border-none bg-transparent cursor-pointer"
              @click="mobileDrawerOpen = false">
              <v-icon small color="grey">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <!-- 1. Four Quick Action Cards -->
        <div class="mb-5">
          <span class="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Shortcuts</span>
          <div class="grid grid-cols-4 gap-2">
            <div v-for="card in quickActionCards" :key="card.title"
              @click="() => { mobileDrawerOpen = false; card.action() }"
              class="p-2.5 bg-gray-50/80 hover:bg-indigo-50/40 rounded-xl border border-gray-100 flex flex-col items-center text-center cursor-pointer transition-all active:scale-95 select-none">
              <div :class="card.bgClass" class="w-9 h-9 rounded-xl flex items-center justify-center mb-1.5 shadow-2xs">
                <v-icon small :color="card.iconColor">{{ card.icon }}</v-icon>
              </div>
              <span class="text-[11px] font-bold text-gray-800 line-clamp-1 leading-tight">{{ card.title }}</span>
            </div>
          </div>
        </div>

        <!-- 2. Activity Breakdown Breakdown -->
        <div>
          <div class="flex items-center justify-between mb-2">
            <span class="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Activity Breakdown</span>
            <v-icon x-small color="grey">mdi-chart-pie</v-icon>
          </div>
          <div class="space-y-2">
            <div v-for="stat in taskStatsList" :key="stat.label"
              class="flex items-center justify-between p-2.5 rounded-xl bg-gray-50 border border-gray-100 text-xs">
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full" :class="stat.dotColor"></span>
                <span class="font-semibold text-gray-700">{{ stat.label }}</span>
              </div>
              <span class="font-extrabold text-gray-900">{{ stat.count }}</span>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Filter Drawer (Config-driven) -->
    <AppTaskFiltersDrawer v-model="filterDrawerOpen" :filters="filters" :appSettings="appSettings"
      :allUseCases="allUseCases" :entities="entities" @apply="onDrawerFilterApply" />

    <!-- Visit / Task History Dialog -->
    <AppTaskHistoryDialog v-model="historyDialogOpen" :task="selectedHistoryTask" />

    <!-- Floating Action Button (FAB) - Create Task -->
    <div class="fixed md:bottom-8 right-4 md:right-8 z-30"
      :style="{ bottom: 'calc(4.75rem + var(--safe-bottom, 0px))' }">
      <button type="button" @click="$router.push('/app/task/create')"
        class="group flex items-center gap-2 text-white font-bold px-4 py-3 sm:px-5 sm:py-3.5 rounded-full shadow-lg hover:opacity-95 hover:-translate-y-0.5 active:translate-y-0 transition-all cursor-pointer border-none"
        :style="{ backgroundColor: $vuetify.theme.currentTheme.employeePrimary }">
        <v-icon small color="white" class="transition-transform group-hover:rotate-90">mdi-plus</v-icon>
        <span class="text-xs sm:text-sm font-extrabold tracking-wide">Create</span>
      </button>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import AppTimeTracker from '@/app/components/timeTracker/AppTimeTracker.vue'
import {
  isFlutterApp,
  notifyClockInStatus,
  registerResumeRefetch,
  sendFlutterTimeSeriesActivity
} from '@/app/utils/flutterBridge'

import AppTaskCard from '@/app/components/cards/AppTaskCard.vue'
import AppTaskFiltersDrawer from '@/app/components/filters/AppTaskFiltersDrawer.vue'
import AppTaskHistoryDialog from '@/app/components/dialogs/AppTaskHistoryDialog.vue'
import {
  getCheckInStatus,
  updateCheckInStatus,
  sendTaskLocation,
  sendLocationTimeSeries,
  getCollectionList,
  getCollectionCases,
  getTaskAnalytics,
  getOrgConfig,
  getEmpAppSettings,
  getAllUseCases,
  getEntities,
  getAttendanceDataV2,
  createCollectionCaseTask
} from '@/app/services/dashboard'
import { getLatLong, getBrowserName } from '@/app/utils/helpers'
import { getActiveUserId } from '@/app/utils/taskFlow'

export default {
  name: 'AppDashboard',
  components: {
    AppTimeTracker,
    AppTaskCard,
    AppTaskFiltersDrawer,
    AppTaskHistoryDialog
  },
  data() {
    return {
      isCheckedIn: false,
      clockInLoading: false,
      tasksLoading: false,
      mobileDrawerOpen: false,
      filterDrawerOpen: false,
      historyDialogOpen: false,
      selectedHistoryTask: null,
      orgConfig: null,
      appSettings: null,
      allUseCases: [],
      entities: [],
      todayWorkSeconds: 0,
      todayBreakSeconds: 0,
      todayWorkingHoursText: '',
      filters: {
        search: '',
        customerSearch: '',
        bktSearch: '',
        sortBy: 'createdAt',
        sortOrder: 'desc',
        assignedTo: 'self',
        caseType: undefined,
        status: [],
        userDefinedCaseStatus: undefined,
        taskStartDate: undefined,
        entity: undefined
      },
      tasks: [],
      statusFilterOptions: [
        { label: 'All', value: 'ALL' },
        { label: 'Open', value: 'OPEN' },
        { label: 'In Progress', value: 'IN_PROGRESS' },
        { label: 'Completed', value: 'COMPLETED' },
        { label: 'Missed', value: 'MISSED' }
      ],
      sortOptions: [
        { label: 'Created At', value: 'createdAt' },
        { label: 'EMI Amount', value: 'emiAmount' },
        { label: 'Tenure', value: 'tenure' },
        { label: 'POS Amount', value: 'pos' }
      ],
      taskStatsList: [
        { label: 'Completed Tasks', count: 0, dotColor: 'bg-emerald-500' },
        { label: 'In Progress', count: 0, dotColor: 'bg-blue-500' },
        { label: 'Pending / Open', count: 0, dotColor: 'bg-amber-500' },
        { label: 'Missed', count: 0, dotColor: 'bg-rose-500' }
      ]
    }
  },
  computed: {
    employeeName() {
      const u = this.$storage?.getUniversal('user') || JSON.parse(localStorage.getItem('user') || 'null') || {}
      const user = u.user || u
      const sysUser = (user.systemUserId && typeof user.systemUserId === 'object') ? user.systemUserId : {}
      return user.first_name || user.firstName || sysUser.first_name || sysUser.firstName || user.username || 'Employee'
    },
    shouldShowClockInOut() {
      if (!this.appSettings) return true
      return this.appSettings.isCheckInAllowed !== false
    },
    customActivitySteps() {
      return this.appSettings?.customActivitySteps || []
    },
    searchFilterVisibility() {
      return this.appSettings?.appDashboardCustomization?.searchFilters || {
        customer: { label: 'Customer', visible: true },
        case: { label: 'Case ID', visible: true },
        bucket: { label: 'Bucket', visible: false }
      }
    },
    searchByFieldsList() {
      const vis = this.searchFilterVisibility
      const list = []
      if (vis?.customer?.visible !== false) {
        list.push({
          label: vis?.customer?.label || 'Customer',
          value: 'customerSearch',
          placeholderTxt: `Enter ${vis?.customer?.label || 'Customer'} Name or Pincode`
        })
      }
      if (vis?.case?.visible !== false) {
        list.push({
          label: vis?.case?.label || 'Case ID',
          value: 'search',
          placeholderTxt: `Enter ${vis?.case?.label || 'Case ID'}`
        })
      }
      if (vis?.bucket?.visible === true) {
        list.push({
          label: vis?.bucket?.label || 'Bucket',
          value: 'bktSearch',
          placeholderTxt: `Enter ${vis?.bucket?.label || 'Bucket'} Name`
        })
      }
      return list
    },
    currentSearchField() {
      if (!this.searchByFieldsList || !this.searchByFieldsList.length) return null
      return this.searchByFieldsList[this.activeSearchByField] || this.searchByFieldsList[0]
    },
    hasSearchOptions() {
      return Boolean(this.searchByFieldsList && this.searchByFieldsList.length > 0 && this.currentSearchField)
    },
    quickActionCards() {
      return [
        {
          title: 'Claims',
          icon: 'mdi-cash-refund',
          iconColor: 'indigo',
          bgClass: 'bg-indigo-50',
          action: () => this.$router.push('/app/claim')
        },
        {
          title: 'Attendance',
          icon: 'mdi-calendar-account',
          iconColor: 'teal',
          bgClass: 'bg-teal-50',
          action: () => this.$router.push('/app/attendance')
        },
        {
          title: 'All Tasks',
          icon: 'mdi-format-list-checks',
          iconColor: 'sky',
          bgClass: 'bg-sky-50',
          action: () => {
            const el = document.getElementById('tasks-schedule-section')
            if (el) el.scrollIntoView({ behavior: 'smooth' })
          }
        },
        {
          title: 'My Profile',
          icon: 'mdi-account-cog-outline',
          iconColor: 'purple',
          bgClass: 'bg-purple-50',
          action: () => this.$router.push('/app/profile')
        }
      ]
    },
    isAnyFilterActive() {
      return (
        (this.filters.status && this.filters.status.length > 0) ||
        !!this.filters.caseType ||
        !!this.filters.userDefinedCaseStatus ||
        !!this.filters.taskStartDate ||
        !!this.filters.entity ||
        (this.filters.assignedTo && this.filters.assignedTo !== 'self')
      )
    },
    activeFilterCount() {
      let count = 0
      if (this.filters.status && this.filters.status.length > 0) count++
      if (this.filters.caseType) count++
      if (this.filters.userDefinedCaseStatus) count++
      if (this.filters.taskStartDate) count++
      if (this.filters.entity) count++
      if (this.filters.assignedTo && this.filters.assignedTo !== 'self') count++
      return count
    },
    filteredTasks() {
      let list = this.tasks || []

      // 1. Status Filter
      if (this.filters.status && this.filters.status.length > 0) {
        list = list.filter((t) => {
          const taskStatus = (t.status || 'OPEN').toUpperCase()
          return this.filters.status.map((s) => s.toUpperCase()).includes(taskStatus)
        })
      }

      // 2. Case Type / Category Filter
      if (this.filters.caseType) {
        list = list.filter((t) => t.subCase === this.filters.caseType || t.caseType === this.filters.caseType)
      }

      // 3. User Defined Outcome Filter
      if (this.filters.userDefinedCaseStatus) {
        list = list.filter((t) => t.userDefinedCaseStatus === this.filters.userDefinedCaseStatus)
      }

      // 4. Date Filter
      if (this.filters.taskStartDate) {
        list = list.filter((t) => {
          const date = t.taskStartDate || t.createdAt || t.startDate
          return date && dayjs(date).format('YYYY-MM-DD') === this.filters.taskStartDate
        })
      }

      // 5. Search Filtering
      const searchVal = this.filters.search?.toLowerCase()?.trim()
      const customerVal = this.filters.customerSearch?.toLowerCase()?.trim()
      const bucketVal = this.filters.bktSearch?.toLowerCase()?.trim()

      if (searchVal) {
        list = list.filter(
          (t) =>
            (t.caseNo && t.caseNo.toLowerCase().includes(searchVal)) ||
            (t.agreementId && t.agreementId.toLowerCase().includes(searchVal)) ||
            (t.taskTitle && t.taskTitle.toLowerCase().includes(searchVal))
        )
      }

      if (customerVal) {
        list = list.filter(
          (t) =>
            (t.customer?.customerFullName && t.customer.customerFullName.toLowerCase().includes(customerVal)) ||
            (t.customer?.pincode && t.customer.pincode.toString().includes(customerVal))
        )
      }

      if (bucketVal) {
        list = list.filter((t) => t.bucket && t.bucket.toLowerCase().includes(bucketVal))
      }

      // 6. Sorting
      if (this.filters.sortBy) {
        const order = this.filters.sortOrder === 'asc' ? 1 : -1
        const key = this.filters.sortBy
        list = [...list].sort((a, b) => {
          let valA = a[key] ?? 0
          let valB = b[key] ?? 0
          if (key === 'createdAt') {
            valA = new Date(valA).getTime()
            valB = new Date(valB).getTime()
          }
          if (valA < valB) return -1 * order
          if (valA > valB) return 1 * order
          return 0
        })
      }

      return list
    }
  },
  mounted() {
    registerResumeRefetch(() => {
      this.fetchCheckInStatus()
      this.fetchTodayAttendanceData()
    })
    this.initDashboard()
  },
  methods: {
    async initDashboard() {
      await Promise.allSettled([
        this.fetchConfigData(),
        this.fetchCheckInStatus(),
        this.fetchTodayAttendanceData(),
        this.fetchTasks(),
        this.fetchStats()
      ])
    },
    async fetchTodayAttendanceData() {
      try {
        const userId = localStorage.getItem('user-id') || this.$storage?.getUniversal('user')?._id
        const todayStr = dayjs().format('YYYY-MM-DD')
        const res = await getAttendanceDataV2({ userId, start: todayStr, end: todayStr })
        const list = res.data?.employeeAttendance || res.data?.data || []
        const todayAtt = Array.isArray(list) ? list.find((a) => dayjs(a.attendanceDate).format('YYYY-MM-DD') === todayStr) : list

        if (todayAtt) {
          // If actualWorkingHrs like "08:15:20" or "00:01:09" exists
          const hrsStr = todayAtt.actualWorkingHrs
          if (hrsStr && typeof hrsStr === 'string' && hrsStr.includes(':')) {
            const parts = hrsStr.split(':').map((p) => parseInt(p, 10) || 0)
            if (parts.length === 3) {
              this.todayWorkSeconds = parts[0] * 3600 + parts[1] * 60 + parts[2]
            } else if (parts.length === 2) {
              this.todayWorkSeconds = parts[0] * 3600 + parts[1] * 60
            }
            this.todayWorkingHoursText = hrsStr
          } else if (todayAtt.checkInOutData && todayAtt.checkInOutData.length > 0) {
            // Compute from checkInOutData punches if actualWorkingHrs is not yet computed
            let totalSeconds = 0
            let lastInTime = null
            todayAtt.checkInOutData.forEach((entry) => {
              if (entry.status === 'CHECKED_IN') {
                lastInTime = dayjs(entry.time)
              } else if (entry.status === 'CHECKED_OUT' && lastInTime) {
                totalSeconds += dayjs(entry.time).diff(lastInTime, 'second')
                lastInTime = null
              }
            })
            if (lastInTime && this.isCheckedIn) {
              totalSeconds += dayjs().diff(lastInTime, 'second')
            }
            if (totalSeconds > 0) {
              this.todayWorkSeconds = totalSeconds
            }
          }
        }
      } catch (err) {
        console.error('Error fetching today attendance data:', err)
      }
    },
    async fetchConfigData() {
      try {
        const [orgRes, appRes, casesRes, entRes] = await Promise.allSettled([
          getOrgConfig(),
          getEmpAppSettings(),
          getAllUseCases(),
          getEntities()
        ])

        if (orgRes.status === 'fulfilled') {
          this.orgConfig = orgRes.value.data?.data || orgRes.value.data
        }
        if (appRes.status === 'fulfilled') {
          this.appSettings = appRes.value.data?.empApp || appRes.value.data?.data || appRes.value.data
        }
        if (casesRes.status === 'fulfilled') {
          this.allUseCases = casesRes.value.data?.data || casesRes.value.data || []
        }
        if (entRes.status === 'fulfilled') {
          this.entities = entRes.value.data?.data || entRes.value.data || []
        }
      } catch (err) {
        console.error('Error fetching config data:', err)
      }
    },
    async fetchCheckInStatus() {
      try {
        const res = await getCheckInStatus()
        const status = res.data?.data?.status || res.data?.status
        this.isCheckedIn = status === 'CHECKED_IN'
        notifyClockInStatus(this.isCheckedIn)
      } catch (err) {
        console.error('Error fetching check-in status:', err)
      }
    },
    async handleClockInToggle() {
      try {
        this.clockInLoading = true
        let coords = { latitude: 0, longitude: 0 }
        let deviceData = {
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          deviceName: getBrowserName()
        }

        try {
          const pos = await getLatLong()
          if (pos?.coords) coords = pos.coords
          if (pos?.deviceData) deviceData = { ...deviceData, ...pos.deviceData }
        } catch (locErr) {
          console.warn('Could not get precise location:', locErr)
        }

        const newStatus = this.isCheckedIn ? 'CHECKED_OUT' : 'CHECKED_IN'
        const currentAction = this.isCheckedIn ? 'checkOut' : 'checkIn'
        const userId = getActiveUserId()

        // 1. Update Attendance / CheckIn Status (/user/v2/checkIn)
        const checkInPayload = {
          latitude: coords.latitude,
          longitude: coords.longitude,
          status: newStatus,
          deviceData
        }

        await updateCheckInStatus(checkInPayload)
        this.isCheckedIn = newStatus === 'CHECKED_IN'

        // 2. Notify Flutter wrapper of check-in status
        notifyClockInStatus(this.isCheckedIn)

        // 3. Prepare Location Model Payload (/user/v2/location)
        const locationPayload = {
          userId,
          latitude: coords.latitude,
          longitude: coords.longitude,
          deviceData,
          activity: {
            activityType: 'CHECK_IN_OUT',
            action: currentAction
          }
        }

        // 4. Prepare Time Series Activity Payload
        const timeSeriesPayload = {
          userId,
          activity: {
            activityType: 'CHECK_IN_OUT',
            action: currentAction
          }
        }

        // 5. Dispatch across Flutter Mode vs Web Mode (identical to finfi_app_v2)
        if (isFlutterApp()) {
          sendFlutterTimeSeriesActivity(timeSeriesPayload)
          await sendTaskLocation(locationPayload).catch((err) =>
            console.warn('Clock in/out location model logging warning in Flutter mode:', err)
          )
        } else {
          const promises = [sendTaskLocation(locationPayload)]
          if (coords.latitude && coords.longitude && userId) {
            promises.push(
              sendLocationTimeSeries({
                ...timeSeriesPayload,
                location: {
                  type: 'Point',
                  coordinates: [coords.longitude, coords.latitude]
                },
                state: 'STILL',
                source: 'WEB',
                deviceName: deviceData.deviceName || getBrowserName()
              })
            )
          }
          await Promise.allSettled(promises)
        }

        this.$toast?.success?.(`Successfully ${this.isCheckedIn ? 'Clocked In' : 'Clocked Out'}!`)
        this.fetchTodayAttendanceData()
      } catch (error) {
        console.error('Error toggling clock-in:', error)
        this.$toast?.error?.(error.response?.data?.message || 'Failed to update check-in status')
      } finally {
        this.clockInLoading = false
      }
    },
    async fetchTasks() {
      try {
        this.tasksLoading = true
        const userId = localStorage.getItem('user-id') || this.$storage?.getUniversal('user')?._id

        // Prepare query payload matching finfi_app_v2 preparePayload
        const params = {
          limit: 50,
          page: 1,
          sortBy: this.filters.sortBy || undefined,
          sortOrder: this.filters.sortOrder || undefined,
          search: this.filters.search || undefined,
          customerSearch: this.filters.customerSearch || undefined,
          bktSearch: this.filters.bktSearch || undefined,
          caseType: this.filters.caseType || undefined,
          userDefinedCaseStatus: this.filters.userDefinedCaseStatus || undefined,
          entity: this.filters.entity || undefined
        }

        if (this.filters.assignedTo === 'self') {
          if (userId) params.assignedTo = userId
        } else if (this.filters.assignedTo) {
          params.assignedTo = this.filters.assignedTo
        }

        if (this.filters.status && this.filters.status.length > 0) {
          params.status = this.filters.status.join(',')
        }

        if (this.filters.taskStartDate) {
          params.taskStartDate = dayjs(this.filters.taskStartDate).format('YYYY-MM-DD')
        }

        // Call collection cases API /collection
        const res = await getCollectionCases(params)
        const data = res.data?.data || res.data?.collections || res.data || []
        const list = Array.isArray(data) ? data : data.collections || data.cases || []

        if (Array.isArray(list) && list.length > 0) {
          this.tasks = list.map((item) => ({
            ...item,
            isCollection: true,
            taskTitle: item.caseNo ? `Case #${item.caseNo}` : item.title || item.taskTitle || 'Case Activity',
            status: item.caseStatus || item.status || 'OPEN'
          }))
        } else {
          // Fallback check on collections tasks endpoint if /collection is empty
          const fallbackRes = await getCollectionList(params).catch(() => null)
          const fallbackData = fallbackRes?.data?.data || fallbackRes?.data?.collections || fallbackRes?.data || []
          const fallbackList = Array.isArray(fallbackData) ? fallbackData : fallbackData.collections || []
          this.tasks = fallbackList.map((item) => ({
            ...item,
            isCollection: true,
            taskTitle: item.caseNo ? `Case #${item.caseNo}` : item.title || item.taskTitle || 'Case Activity',
            status: item.caseStatus || item.status || 'OPEN'
          }))
        }
      } catch (err) {
        console.error('Error loading collection cases:', err)
        // Try fallback to collection tasks endpoint
        try {
          const userId = localStorage.getItem('user-id') || this.$storage?.getUniversal('user')?._id
          const fallbackRes = await getCollectionList({ assignedTo: userId, limit: 50 })
          const list = fallbackRes.data?.collections || fallbackRes.data?.data || fallbackRes.data || []
          if (Array.isArray(list)) {
            this.tasks = list.map((item) => ({
              ...item,
              isCollection: true,
              taskTitle: item.caseNo ? `Case #${item.caseNo}` : item.title || item.taskTitle || 'Case Activity',
              status: item.caseStatus || item.status || 'OPEN'
            }))
          }
        } catch (fallbackErr) {
          console.error('Fallback fetch failed:', fallbackErr)
        }
      } finally {
        this.tasksLoading = false
      }
    },
    async fetchStats() {
      try {
        const userId = localStorage.getItem('user-id') || this.$storage?.getUniversal('user')?._id
        const params = userId ? { assignedTo: userId } : {}
        const res = await getTaskAnalytics(params)
        const stats = res.data?.stats || res.data?.data?.stats || res.data?.data || res.data || []
        if (Array.isArray(stats)) {
          const completed = stats.find((a) => a._id === 'COMPLETED')?.totalTasks || 0
          const inProgress = stats.find((a) => a._id === 'IN_PROGRESS')?.totalTasks || 0
          const pending = stats.find((a) => a._id === 'PENDING' || a._id === 'OPEN')?.totalTasks || 0
          const missed = stats.find((a) => a._id === 'MISSED')?.totalTasks || 0

          this.taskStatsList = [
            { label: 'Completed Tasks', count: completed, dotColor: 'bg-emerald-500' },
            { label: 'In Progress', count: inProgress, dotColor: 'bg-blue-500' },
            { label: 'Pending / Open', count: pending, dotColor: 'bg-amber-500' },
            { label: 'Missed', count: missed, dotColor: 'bg-rose-500' }
          ]
        }
      } catch (err) {
        console.error('Error loading task stats:', err)
      }
    },
    handleStatusPillClick(value) {
      if (value === 'ALL') {
        this.filters.status = []
      } else {
        const index = this.filters.status.indexOf(value)
        if (index > -1) {
          this.filters.status.splice(index, 1)
        } else {
          this.filters.status = [value]
        }
      }
    },
    toggleSortOrder() {
      this.filters.sortOrder = this.filters.sortOrder === 'asc' ? 'desc' : 'asc'
      this.fetchTasks()
    },
    onDrawerFilterApply(updatedFilters) {
      this.filters = { ...this.filters, ...updatedFilters }
      this.fetchTasks()
    },
    applyFiltersAndFetch() {
      this.fetchTasks()
    },
    onSearchInput() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.fetchTasks()
      }, 400)
    },
    resetFilters() {
      this.filters = {
        search: '',
        customerSearch: '',
        bktSearch: '',
        sortBy: 'createdAt',
        sortOrder: 'desc',
        assignedTo: 'self',
        caseType: undefined,
        status: [],
        userDefinedCaseStatus: undefined,
        taskStartDate: undefined,
        entity: undefined
      }
      this.fetchTasks()
    },
    async openTaskDetails(task) {
      if (!task) return
      if (!this.isCheckedIn) {
        this.$toast?.warning?.('Please Clock-In first to continue this task.')
        return
      }

      try {
        const userId = localStorage.getItem('user-id') || this.$storage?.getUniversal('user')?._id
        const isCollection = !!task.isCollection || !!task.case || !!task.caseNo
        const caseId = task.caseId || task.case?._id || task._id || task.id

        // Check if there is an active pending/in-progress task for this user
        let activeTaskId = null
        const taskList = task.tasks || []
        const userTask = taskList.find(
          (t) => (t.assignedTo?._id === userId || t.assignedTo === userId) && t.taskStatus !== 'COMPLETED'
        )

        if (userTask) {
          activeTaskId = userTask._id || userTask.id
        } else if (isCollection && (!taskList.length || !userTask)) {
          // Auto-schedule task for collection if no pending task exists (matching mobile app onScheduleNextVisit)
          const latestTask = taskList[0]
          const now = dayjs()
          const newTaskPayload = {
            autoAssigned: 'true',
            taskTitle: latestTask?.taskTitle || task.caseNo || 'Collection Task',
            taskDescription: latestTask?.taskDescription || '',
            taskStartDateTime: now.format('YYYY-MM-DDTHH:mm:ss'),
            taskEndDateTime: now.format('YYYY-MM-DDT23:59:00'),
            assignedTo: userId,
            meetingDetails: {},
            isCollection: true,
            case: { _id: caseId }
          }

          try {
            const createdRes = await createCollectionCaseTask(newTaskPayload).catch(() => null)
            const createdTask = createdRes?.data?.task || createdRes?.data?.data
            if (createdTask?._id) {
              activeTaskId = createdTask._id
            }
          } catch (createErr) {
            console.warn('Auto schedule task creation error:', createErr)
          }
        }

        const targetTaskId = activeTaskId || caseId
        this.$router.push({
          name: 'AppStartJob',
          params: { taskId: targetTaskId },
          query: { isCollection: isCollection }
        })
      } catch (err) {
        console.error('Error opening task details:', err)
        const fallbackId = task.caseId || task.case?._id || task._id || task.id
        this.$router.push({
          name: 'AppStartJob',
          params: { taskId: fallbackId },
          query: { isCollection: !!task.isCollection }
        })
      }
    },
    openTaskHistory(task) {
      if (!task) return
      this.selectedHistoryTask = task
      this.historyDialogOpen = true
    }
  }
}
</script>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

::v-deep .custom-dense-select .v-input__control {
  min-height: 36px !important;
}

::v-deep .custom-dense-select .v-input__slot {
  min-height: 36px !important;
  margin-bottom: 0 !important;
  padding: 0 10px !important;
}

::v-deep .custom-dense-select .v-select__selections {
  padding: 0 !important;
  font-size: 12px !important;
}
</style>
